import React from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon, CreditCardIcon, FingerprintIcon, LayoutGridIcon, SparklesIcon, UsersIcon, BoxIcon } from "lucide-react";
import { useLanguage } from "../LanguageContext";
import { sectors, Bi } from "../../data/sectors";
import { sectorsFoundation as c } from "../../data/sectorsPage";
import { SECTOR_ICONS } from "./sectorIcons";
const EASE = [0.23, 1, 0.32, 1] as const;
const reveal = {
  initial: {
    opacity: 0,
    y: 12
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.3,
    ease: EASE
  }
};
const STAGE_ICONS: Record<(typeof c.stages)[number]['key'], BoxIcon> = {
  identity: FingerprintIcon,
  relations: UsersIcon,
  card: CreditCardIcon,
  services: LayoutGridIcon,
  atna: SparklesIcon
};
export function SectorsFoundation() {
  const {
    lang
  } = useLanguage();
  const pick = (b: Bi) => lang === 'FR' ? b.fr : b.en;
  return <section className="relative overflow-hidden bg-white border-t border-[#EEF0F4] px-6 py-20 lg:py-24" aria-labelledby="sectors-foundation-title" data-id="element-6602" data-mp="qa1h786df1al">
      <div className="max-w-[1280px] mx-auto" data-id="element-6603" data-mp="v9dzdfmark6u">
        <motion.div {...reveal} className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-8 lg:gap-16 items-end" data-id="element-6604" data-mp="1087etdl59l9n">
          <div data-id="element-6605" data-mp="14z60ksowjbka">
            <p className="font-sans text-[13px] font-medium tracking-[0.3em] uppercase text-[#4F46E5]" data-id="element-6606" data-mp="14zpfs1n63lum">{pick(c.eyebrow)}</p>
            <h2 id="sectors-foundation-title" className="font-serif text-[2rem] sm:text-[2.5rem] lg:text-[2.8rem] leading-[1.06] tracking-tight mt-5" data-id="element-6607" data-mp="19xh8gxvmzbzv">
              {c.headline.map(l => <span key={l.text.fr} className={`block ${l.accent ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6608" data-mp="1izgs7xy1cu6i">{pick(l.text)}</span>)}
            </h2>
          </div>
          <p className="max-w-[540px] font-sans text-[15.5px] md:text-[16.5px] text-[#4A5268] leading-relaxed lg:pb-1" data-id="element-6609" data-mp="3kps7ndi8qd1b">{pick(c.body)}</p>
        </motion.div>

        <motion.div {...reveal} className="mt-14 lg:mt-16" data-id="element-6610" data-mp="3ppetucg1r3m9">
          {/* Different ecosystems on top… */}
          <ul className="flex flex-wrap justify-center gap-2" aria-label={lang === 'FR' ? 'Écosystèmes' : 'Ecosystems'} data-id="element-6611" data-mp="3hoh0akytdojx">
            {sectors.map(s => {
            const Icon = SECTOR_ICONS[s.key];
            return <li key={s.key} className="inline-flex items-center gap-2 rounded-full border border-[#ECEBF5] bg-[#FBFBFE] px-3.5 py-1.5 font-sans text-[12.5px] text-[#3A4260]" data-id="element-6612" data-mp="3rka7rpxoeruu">
                  <Icon className="w-3.5 h-3.5 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-6613" data-mp="4g4493m8j7vf" />
                  {pick(s.name)}
                </li>;
          })}
          </ul>
          <div className="flex justify-center" aria-hidden="true" data-id="element-6614" data-mp="e6f7sp20bf1s">
            <span className="w-px h-10 bg-[#D9D7F4]" data-id="element-6615" data-mp="98nj03wns91t" />
          </div>

          {/* …one shared foundation underneath */}
          <ol className="rounded-3xl border border-[#E7E6F5] bg-[#F8F8FE] px-5 md:px-8 py-7 md:py-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-y-7 md:gap-x-6" data-id="element-6616" data-mp="o4v3l5bzbchm">
            {c.stages.map((s, i) => {
            const Icon = STAGE_ICONS[s.key];
            const atna = s.key === 'atna';
            return <li key={s.key} className="relative flex md:flex-col gap-4 md:gap-0 min-w-0" data-id="element-6617" data-mp="16u86gzc6e4dl" data-mpi={i}>
                  <div className="flex items-center md:w-full" data-id="element-6618" data-mp="suyr21vsneao">
                    <span className={`w-11 h-11 rounded-full flex items-center justify-center shrink-0 ${atna ? 'bg-[#4F46E5]' : 'bg-white border border-[#E2E0FA]'}`} data-id="element-6619" data-mp="xta2gh1qlwv0">
                      <Icon className={`w-5 h-5 ${atna ? 'text-white' : 'text-[#4F46E5]'}`} strokeWidth={1.6} aria-hidden="true" data-id="element-6620" data-mp="j32tqt1j7s7i" />
                    </span>
                    {i < c.stages.length - 1 && <span className="hidden lg:flex flex-1 items-center pl-3" aria-hidden="true" data-id="element-6621" data-mp="a4z5hoz5whc0">
                        <span className="flex-1 h-px bg-[#D9D7F4]" data-id="element-6622" data-mp="f3k6vrq5h721" />
                        <ArrowRightIcon className="w-3.5 h-3.5 text-[#8B85F0] -ml-1" strokeWidth={1.8} data-id="element-6623" data-mp="502e7lihj6j" />
                      </span>}
                  </div>
                  <div className="min-w-0 md:mt-4" data-id="element-6624" data-mp="56pabedftgqe">
                    <p className="font-sans text-[10.5px] font-medium text-[#8A8FB8] tabular-nums" data-id="element-6625" data-mp="18fs5qdmu6gox">{String(i + 1).padStart(2, '0')}</p>
                    <h3 className={`mt-0.5 font-sans text-[12.5px] font-semibold tracking-[0.1em] uppercase ${atna ? 'text-[#4F46E5]' : 'text-[#121A3A]'}`} data-id="element-6626" data-mp="13h7bcuu9mr8e">
                      {pick(s.label)}
                    </h3>
                    <p className="mt-1.5 font-sans text-[13.5px] text-[#4A5268] leading-snug" data-id="element-6627" data-mp="torszwf240mz">{pick(s.text)}</p>
                  </div>
                </li>;
          })}
          </ol>
        </motion.div>
      </div>
    </section>;
}